import React, { createContext, useContext, useRef, useCallback } from 'react';
import { KatToastGroup } from '@amzn/katal-react';
import { Toast } from '@amzn/katal-components/es/toast-group';
import { v4 as uuid } from 'uuid';

/**
 * Configuration options for creating a toast notification
 *
 * Refer to {@link https://katal.amazon.dev/design-system/components/toast-group/|Documentation}
 */
interface ToastOptions {
  /** The message to display in the toast */
  description: string;
  /** The type of toast notification */
  variant?: 'info' | 'success' | 'warning' | 'danger';
  /** Whether the toast should remain visible until manually dismissed */
  persistent?: boolean;
}

/**
 * Context interface for toast functionality
 */
export interface ToastContextType {
  /** Adds a new toast notification */
  addToast: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

/**
 * Provider component that allows child components to display toast notifications
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const toastRef = useRef<KatToastGroup.Element | null>(null);

  const addToast = useCallback((options: ToastOptions) => {
    const toast: Toast = {
      id: uuid(),
      ...options,
    };
    toastRef.current?.openToast(toast);
  }, []);

  return (
    <ToastContext.Provider value={{ addToast }}>
      <KatToastGroup ref={toastRef} />
      {children}
    </ToastContext.Provider>
  );
};

/**
 * Hook to access toast functionality
 * @returns {ToastContextType} Object containing addToast function
 * @throws {Error} If used outside of ToastProvider
 */
const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export { type ToastOptions, ToastProvider, useToast };
